import { customElement, property, state } from "lit/decorators.js";
import { BaseElement } from "../../utils/ui-components.js";
import { Api, User } from "../../api.js";
import { html } from "lit";

@customElement("user-profile")
export class UserProfile extends BaseElement {
    @property({ type: String })
    userId?: string;

    @state()
    private user?: User;

    @state()
    private loading = true;

    @state()
    private error = "";

    @state()
    private currentUser?: User;

    async firstUpdated() {
        try {
            // Check if current user is admin first
            this.currentUser = await Api.me();
            if (this.currentUser.role !== "admin") {
                location.href = "/";
                return;
            }

            const userId = location.pathname.split("/").pop();
            if (!userId) return;

            this.user = await Api.getUser(userId);
        } catch (e) {
            location.href = "/";
        } finally {
            this.loading = false;
        }
    }

    render() {
        if (this.loading) {
            return html`<div class="p-4">Loading...</div>`;
        }

        if (this.error || !this.user) {
            return html`<div class="p-4 text-red-600">${this.error || "User not found"}</div>`;
        }

        return html`
            <div class="min-h-screen bg-gray-100">
                <div class="max-w-7xl mx-auto py-6">
                    <div class="grid grid-cols-1 gap-6">
                        <!-- User Info Card -->
                        <h1 class="text-2xl font-bold">User Profile: ${this.user.name}</h1>
                        <div class="bg-white rounded-lg shadow overflow-hidden">
                            <dl class="divide-y divide-gray-200">
                                <div class="px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                                    <dt class="text-sm font-medium text-gray-500">Name</dt>
                                    <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">${this.user.name}</dd>
                                </div>
                                <div class="px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                                    <dt class="text-sm font-medium text-gray-500">Email</dt>
                                    <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">${this.user.email}</dd>
                                </div>
                                <div class="px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                                    <dt class="text-sm font-medium text-gray-500">Role</dt>
                                    <dd class="mt-1 text-sm sm:mt-0 sm:col-span-2">
                                        <span
                                            class="px-2 py-1 text-xs rounded-full ${this.user.role === "admin"
                                                ? "bg-purple-100 text-purple-800"
                                                : "bg-gray-100 text-gray-800"}"
                                        >
                                            ${this.user.role}
                                        </span>
                                    </dd>
                                </div>
                                <div class="px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                                    <dt class="text-sm font-medium text-gray-500">Created</dt>
                                    <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        ${new Date(this.user.created_at).toLocaleString()}
                                    </dd>
                                </div>
                            </dl>
                        </div>

                        <!-- Token Usage Chart -->
                        <div class="bg-white rounded-lg shadow p-6">
                            <h3 class="text-lg font-medium text-gray-900 mb-4">Token Usage</h3>
                            <token-usage-chart .userId=${this.user.id}></token-usage-chart>
                        </div>

                        <!-- Chat Sessions -->
                        <div class="bg-white rounded-lg shadow p-6">
                            <h3 class="text-lg font-medium text-gray-900 mb-4">Chat Sessions</h3>
                            <chat-sessions .userId=${this.user.id}></chat-sessions>
                        </div>

                        <!-- Search History -->
                        <div class="bg-white rounded-lg shadow p-6">
                            <h3 class="text-lg font-medium text-gray-900 mb-4">Search History</h3>
                            <search-events .userId=${this.user.id}></search-events>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
}
